import {
    Component,
    DOCUMENT,
    DestroyRef,
    OnInit,
    Renderer2,
    ViewEncapsulation,
    inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { FuseConfig, FuseConfigService } from "@fuse/services/config";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { FusePlatformService } from "@fuse/services/platform";
import { FUSE_VERSION } from "@fuse/version";
import { combineLatest, filter, map } from "rxjs";
import { EmptyLayoutComponent } from "./layouts/empty/empty.component";
import { ClassicLayoutComponent } from "./layouts/vertical/classic/classic.component";

@Component({
    selector: "layout",
    templateUrl: "./layout.component.html",
    styleUrls: ["./layout.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [EmptyLayoutComponent, ClassicLayoutComponent]
})
export class LayoutComponent implements OnInit {
    config: FuseConfig;
    layout: string;
    scheme: "dark" | "light";
    theme: string;
    private _activatedRoute = inject(ActivatedRoute);
    private _document = inject<Document>(DOCUMENT);
    private _renderer2 = inject(Renderer2);
    private _router = inject(Router);
    private _fuseConfigService = inject(FuseConfigService);
    private _fuseMediaWatcherService = inject(FuseMediaWatcherService);
    private _fusePlatformService = inject(FusePlatformService);
    private _destroyRef = inject(DestroyRef);

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Set the theme and scheme based on the configuration
        combineLatest([
            this._fuseConfigService.config$,
            this._fuseMediaWatcherService.onMediaQueryChange$([
                "(prefers-color-scheme: dark)",
                "(prefers-color-scheme: light)"
            ])
        ])
            .pipe(
                takeUntilDestroyed(this._destroyRef),
                map(([config, mql]) => {
                    const options: { scheme: "dark" | "light"; theme: string } = {
                        scheme:
                            config.scheme === "auto"
                                ? mql.breakpoints["(prefers-color-scheme: dark)"]
                                    ? "dark"
                                    : "light"
                                : config.scheme,
                        theme: config.theme
                    };

                    return options;
                })
            )
            .subscribe((options) => {
                // Store the options
                this.scheme = options.scheme;
                this.theme = options.theme;

                // Update the scheme and theme
                this._updateScheme();
                this._updateTheme();
            });

        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((config: FuseConfig) => {
                // Store the config
                this.config = config;

                // Update the layout
                this._updateLayout();
            });

        // Subscribe to NavigationEnd event
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe(() => {
                // Update the layout
                this._updateLayout();
            });

        // Set the app version
        this._renderer2.setAttribute(
            this._document.querySelector("[ng-version]"),
            "fuse-version",
            FUSE_VERSION
        );

        // Set the OS name
        this._renderer2.addClass(
            this._document.body,
            this._fusePlatformService.osName
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the selected layout
     */
    private _updateLayout(): void {
        // Get the current activated route
        let route = this._activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        // 1. Set the layout from the config
        this.layout = this.config.layout;

        // 2. Get the query parameter from the current route and
        // set the layout and save the layout to the config
        const layoutFromQueryParam = route.snapshot.queryParamMap.get("layout");
        if (layoutFromQueryParam) {
            this.layout = layoutFromQueryParam;
            if (this.config) {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Iterate through the paths and change the layout as we find
        // a config for it.
        //
        // The reason we do this is that there might be empty grouping
        // paths or componentless routes along the path. Because of that,
        // we cannot just assume that the layout configuration will be
        // in the last path's config or in the first path's config.
        //
        // So, we get all the paths that matched starting from root all
        // the way to the current activated route, walk through them one
        // by one and change the layout as we find the layout config. This
        // way, layout configuration can live anywhere within the path and
        // we won't miss it.
        //
        // Also, this will allow overriding the layout in any time so we
        // can have different layouts for different routes.
        const paths = route.pathFromRoot;
        paths.forEach((path) => {
            // Check if there is a 'layout' data
            if (
                path.routeConfig &&
                path.routeConfig.data &&
                path.routeConfig.data.layout
            ) {
                // Set the layout
                this.layout = path.routeConfig.data.layout;
            }
        });
    }

    /**
     * Update the selected scheme
     *
     * @private
     */
    private _updateScheme(): void {
        // Remove class names for all schemes
        this._document.body.classList.remove("light", "dark");

        // Add class name for the currently selected scheme
        this._document.body.classList.add(this.scheme);
    }

    /**
     * Update the selected theme
     *
     * @private
     */
    private _updateTheme(): void {
        // Find the class name for the previously selected theme and remove it
        this._document.body.classList.forEach((className: string) => {
            if (className.startsWith("theme-")) {
                this._document.body.classList.remove(
                    className,
                    className.split("-")[1]
                );
            }
        });

        // Add class name for the currently selected theme
        this._document.body.classList.add(this.theme);
    }
}
